import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import type { Screenshot } from '../types';
import { format } from 'date-fns';

interface ScreenshotGalleryProps {
  screenshots: Screenshot[];
}

export default function ScreenshotGallery({ screenshots }: ScreenshotGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (screenshots.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Keine Screenshots vorhanden
      </div>
    );
  }

  const typeLabels: Record<string, string> = {
    homepage: 'Startseite',
    admin: 'Admin-Bereich',
    custom: 'Benutzerdefiniert',
  };

  const handlePrev = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((prev) =>
        prev === 0 ? screenshots.length - 1 : prev! - 1
      );
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((prev) =>
        prev === screenshots.length - 1 ? 0 : prev! + 1
      );
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {screenshots.map((screenshot, index) => (
          <div
            key={screenshot.id}
            className="group relative bg-gray-100 rounded-lg overflow-hidden aspect-video cursor-pointer"
            onClick={() => setSelectedIndex(index)}
          >
            <img
              src={screenshot.url}
              alt={`${screenshot.type} screenshot`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors">
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
              <p className="text-white text-sm font-medium">
                {typeLabels[screenshot.type] || screenshot.type}
              </p>
              <p className="text-white/70 text-xs">
                {format(new Date(screenshot.createdAt), 'dd.MM.yyyy HH:mm')}
              </p>
            </div>
          </div>
        ))}
      </div>

      {selectedIndex !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={() => setSelectedIndex(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors"
            onClick={() => setSelectedIndex(null)}
          >
            <X className="w-8 h-8" />
          </button>

          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          <div
            className="max-w-5xl max-h-[80vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={screenshots[selectedIndex].url}
              alt={`${screenshots[selectedIndex].type} screenshot`}
              className="max-w-full max-h-[70vh] object-contain"
            />
            <div className="mt-4 text-center">
              <p className="text-white font-medium">
                {typeLabels[screenshots[selectedIndex].type] ||
                  screenshots[selectedIndex].type}
              </p>
              <p className="text-white/70 text-sm">
                {format(
                  new Date(screenshots[selectedIndex].createdAt),
                  'dd.MM.yyyy HH:mm:ss'
                )}
              </p>
              <p className="text-white/50 text-xs mt-1">
                {selectedIndex + 1} / {screenshots.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
